import {
  useCallback,
  useEffect,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (
        siteKey: string,
        options: { action: string },
      ) => Promise<string>;
    };
  }
}

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY as
  | string
  | undefined;

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);

  const loadRecaptcha = useCallback(() => {
    if (isRecaptchaReady || !RECAPTCHA_SITE_KEY) return;
    if (window.grecaptcha) {
      setIsRecaptchaReady(true);
      return;
    }
    const scriptId = "recaptcha-script";
    if (document.getElementById(scriptId)) return;
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsRecaptchaReady(true);
    document.body.appendChild(script);
  }, [isRecaptchaReady]);

  useEffect(() => {
    loadRecaptcha();
  }, [loadRecaptcha]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.email ||
      !formData.subject ||
      !formData.message
    ) {
      setSubmitStatus("error");
      return;
    }

    if (formData.message.length > 500) {
      alert("メッセージは500文字以内で入力してください。");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");
    setErrorMessage(null);

    try {
      if (!RECAPTCHA_SITE_KEY) {
        throw new Error(
          "reCAPTCHAの設定がありません。管理者に連絡してください。",
        );
      }

      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(
          () => reject(new Error("reCAPTCHAの初期化に失敗しました。")),
          5000,
        );
        window.grecaptcha?.ready(() => {
          clearTimeout(timeout);
          resolve();
        });
      });

      const recaptchaToken = await window.grecaptcha?.execute(
        RECAPTCHA_SITE_KEY,
        {
          action: "contact",
        },
      );

      if (!recaptchaToken) {
        throw new Error("reCAPTCHAトークンの取得に失敗しました。");
      }

      const response = await fetch("/api/contact.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ ...formData, token: recaptchaToken }),
      });

      if (response.ok) {
        const data = (await response.json()) as {
          success?: boolean;
          message?: string;
        };
        if (data.success) {
          setSubmitStatus("success");
          setFormData({ name: "", email: "", subject: "", message: "" });
        } else {
          setSubmitStatus("error");
          setErrorMessage(
            data.message || "送信に失敗しました。もう一度お試しください。",
          );
        }
      } else {
        let msg = "送信に失敗しました。もう一度お試しください。";
        try {
          const data = (await response.json()) as { message?: string };
          if (data.message) msg = data.message;
        } catch {
          // ignore parse errors
        }
        setErrorMessage(msg);
        setSubmitStatus("error");
      }
    } catch (err) {
      setSubmitStatus("error");
      setErrorMessage(
        err instanceof Error
          ? err.message
          : "送信に失敗しました。通信環境をご確認の上、再度お試しください。",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section
      id="contact"
      className="relative overflow-hidden bg-red-50 px-6 py-16 md:px-12 md:py-24 lg:px-24"
    >
      <div className="relative z-10 mx-auto max-w-7xl">
        <div className="grid grid-cols-1 gap-10 md:gap-16 lg:grid-cols-2">
          <div className="fade-in-section">
            <h2 className="mb-4 text-4xl font-semibold text-gray-900 md:mb-6 md:text-6xl">
              <span className="text-red-500">C</span>ontact
            </h2>
            <p className="mb-8 text-base text-gray-600 md:mb-12 md:text-lg">
              お気軽にお問い合わせください。
              <br />
              ご相談は無料です。
            </p>

            <div className="space-y-5 rounded-2xl border-2 border-white bg-white/80 p-6 shadow-lg backdrop-blur-sm md:p-8">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-50">
                  <i className="ri-customer-service-2-line text-xl text-red-500"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">
                    こんなご相談に
                  </div>
                  <div className="text-sm text-gray-500">
                    24時間以内にご返信します
                  </div>
                </div>
              </div>

              <ul className="space-y-3 text-sm leading-relaxed text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-red-500"></i>
                  <span>開発・改修の概算見積もりやスケジュール感</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-red-500"></i>
                  <span>要件整理・技術選定の壁打ち</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-red-500"></i>
                  <span>継続的な運用・保守体制のご相談</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="fade-in-section">
            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-2xl border-2 border-gray-200 bg-white p-8 shadow-lg md:p-12"
            >
              <div className="mb-5">
                <label
                  htmlFor="name"
                  className="mb-2 block text-sm font-medium text-gray-900"
                >
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border-2 border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-red-500 focus:outline-none"
                  placeholder="山田 太郎"
                />
              </div>
              <div className="mb-5">
                <label
                  htmlFor="email"
                  className="mb-2 block text-sm font-medium text-gray-900"
                >
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border-2 border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-red-500 focus:outline-none"
                  placeholder="example@email.com"
                />
              </div>
              <div className="mb-5">
                <label
                  htmlFor="subject"
                  className="mb-2 block text-sm font-medium text-gray-900"
                >
                  件名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border-2 border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-red-500 focus:outline-none"
                  placeholder="お問い合わせ内容"
                />
              </div>
              <div className="mb-6">
                <label
                  htmlFor="message"
                  className="mb-2 block text-sm font-medium text-gray-900"
                >
                  メッセージ <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full resize-none rounded-lg border-2 border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-red-500 focus:outline-none"
                  placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                ></textarea>
                <div className="mt-1 text-right text-xs text-gray-500">
                  {formData.message.length}/500
                </div>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full cursor-pointer whitespace-nowrap rounded-lg bg-red-500 py-3.5 font-semibold text-white transition-all duration-300 hover:bg-red-600 hover:shadow-lg hover:shadow-red-500/30 disabled:cursor-not-allowed disabled:opacity-50 md:py-4"
              >
                {isSubmitting ? "送信中..." : "送信する"}
              </button>
              <p className="mt-3 text-center text-[11px] leading-relaxed text-gray-500 md:text-left">
                このサイトは reCAPTCHA によって保護されており、Googleの
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-red-500 underline"
                >
                  プライバシーポリシー
                </a>
                と
                <a
                  href="https://policies.google.com/terms"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-red-500 underline"
                >
                  利用規約
                </a>
                が適用されます。
              </p>
              {submitStatus === "success" && (
                <div className="mt-4 rounded-lg border-2 border-green-500 bg-green-50 p-4 text-center text-sm text-green-600">
                  お問い合わせありがとうございます。折り返しご連絡いたします。
                </div>
              )}
              {submitStatus === "error" && (
                <div className="mt-4 rounded-lg border-2 border-red-500 bg-red-50 p-4 text-center text-sm text-red-600">
                  {errorMessage ||
                    "送信に失敗しました。もう一度お試しください。"}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Contact;
